import { Button } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { DialogListener } from '@components/Dialog/Dialog';
import Icon from '@components/Icon/Icon';

import { getObservationConfig } from '@constants/fhir';

import { useHealthChart } from '@hooks/useHealthChart';
import { useParameter } from '@hooks/useParameter';
import { useSession } from '@hooks/useSession';

import NormalRangeChip from '../Header/NormalRangeChip';
import ParameterContent from './index';

const messages = defineMessages({
  title: {
    id: 'insights.range.title',
    defaultMessage: '{code} {params} ({type})',
  },
  custom: {
    id: 'insights.range.custom',
    defaultMessage: 'Custom',
  },
  standard: {
    id: 'insights.range.standard',
    defaultMessage: 'Standard',
  },
  range: {
    id: 'insights.range.text',
    defaultMessage: 'Ranges',
  },
  parameter: {
    id: 'insights.parameter.text',
    defaultMessage: 'Parameters',
  },
  percentage: {
    id: 'healthTracker.detail.kpi.percentage',
    defaultMessage: '% in parameters',
  },
  question: {
    id: 'insights.range.question',
    defaultMessage: 'What are the ranges?',
  },
});

interface WrappedComponentProps {
  handleOpen: () => void;
}

function withParameterDialog(
  WrappedComponent: React.ComponentType<WrappedComponentProps>
) {
  return () => {
    const { formatMessage } = useIntl();
    const { code } = useHealthChart();
    const { isStandard } = useParameter();

    const {
      state: { isClient },
    } = useSession();

    const codeLabel = formatMessage({
      id: getObservationConfig(code)?.messageId,
    });
    const title = formatMessage(messages.title, {
      code: codeLabel,
      params: formatMessage(isClient ? messages.range : messages.parameter),
      type: formatMessage(isStandard ? messages.standard : messages.custom),
    });

    return (
      <DialogListener content={<ParameterContent />} title={title}>
        {({ handleOpen }) => <WrappedComponent handleOpen={handleOpen} />}
      </DialogListener>
    );
  };
}

const ChipComponent = ({ handleOpen }: WrappedComponentProps) => {
  return <NormalRangeChip onClick={handleOpen} />;
};

const ButtonComponent = ({ handleOpen }: WrappedComponentProps) => {
  const { formatMessage } = useIntl();
  return (
    <Button
      endIcon={<Icon name="circle-question" type="far" />}
      onClick={handleOpen}
      variant="text"
      sx={{
        textTransform: 'uppercase',
      }}
    >
      {formatMessage(messages.percentage)}
    </Button>
  );
};

const IconComponent = ({ handleOpen }: WrappedComponentProps) => {
  return (
    <Icon
      color="text.primary"
      name="circle-question"
      onClick={handleOpen}
      size="lg"
      type="far"
    />
  );
};

const ChipParameterDialog = withParameterDialog(ChipComponent);
const ButtonParameterDialog = withParameterDialog(ButtonComponent);
const IconParameterDialog = withParameterDialog(IconComponent);

export { ButtonParameterDialog, ChipParameterDialog, IconParameterDialog };
